import React, { useMemo } from "react";
import { useMutation } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { Form } from "react-final-form";
import { Box, Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MUIFormSectionWFields } from "@upptic/module-ui";

import { nonAuthRoutes } from "v2/routes/routesGroups/nonAuthRoutes";
import { RESET_PASSWORD } from "api/mutations/auth";
import { useRequestErrorHandler } from "v2/hooks/useRequestErrorHandler";
import { AuthSection } from "components/auth";
import validators from "utils/validators";

export interface FormData {
  email: string;
}

function ForgotPasswordContainer() {
  const { t } = useTranslation("auth");
  const { t: tg } = useTranslation("general");
  const history = useHistory();
  const { state } = useLocation<{ userEmail?: string } | undefined>();

  const [forgotPassword] = useMutation(RESET_PASSWORD);

  const handleError = useRequestErrorHandler();

  const defaultState: FormData = useMemo(
    () => ({
      email: state?.userEmail || "",
    }),
    [state?.userEmail],
  );

  const handleSubmit = async (data: FormData) => {
    const forgotPasswordResponse = await handleError(
      forgotPassword({
        variables: data,
      }),
    );

    forgotPasswordResponse &&
      history.push({
        pathname: nonAuthRoutes.AUTH_FORGOT_PASSWORD_CONFIRM.getUrlWithParams(),
        state: {
          userEmail: data.email,
        },
      });
  };

  const fields = [
    {
      name: "email",
      label: t<string>("email"),
      disablePwManagerAutofill: false,
      placeholder: "Enter Your Email",
      validate: validators.composeValidators(
        false,
        validators.required,
        validators.email,
      ),
      xs: 12,
    },
  ];

  return (
    <Form onSubmit={handleSubmit} initialValues={defaultState}>
      {({ handleSubmit: submit, submitting, values }) => {
        return (
          <form onSubmit={submit} autoComplete="off">
            <AuthSection title="Reset Password">
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                sx={{ minWidth: 420 }}
              >
                <MUIFormSectionWFields disablePadding fields={fields} />
                <Stack
                  sx={{ width: "100%" }}
                  spacing={2}
                  pt={2}
                  direction="row"
                  justifyContent="flex-end"
                >
                  <Button
                    variant="contained"
                    sx={{ minWidth: "130px" }}
                    type="submit"
                    disabled={submitting}
                  >
                    {tg<string>("submit")}
                  </Button>
                </Stack>
              </Box>
            </AuthSection>
          </form>
        );
      }}
    </Form>
  );
}

export default ForgotPasswordContainer;
